import React from "react";
import { shouldShowMappedArtworkProof } from "./proofVisibility";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-hot-toast";
import ProofReviewCard from "../../proofs/ProofReviewCard";
import PdfViewerDialog from "../../proofs/PdfViewerDialog";
import SmartProofPreview from "../../proofs/SmartProofPreview";
import { getStoredEmail, isPdfUrl, isValidEmail, setStoredEmail } from "../../proofs/proofRowUtils";
import { patchArtworkProofMappingClientApproval } from "../../../services/orders";
import styles from "./index.module.css";

const TEXT_GREY = "#55656d";
const IMAGE_CDN = "https://api.asicentral.com/v1/";
const IMG_EXT_RX = /\.(png|jpe?g|gif|webp|bmp|svg)(?:$|[?#])/i;
const isPdfFile = (s = "") => /\.pdf(?:$|[?#])/i.test(s);

const getExt = (url = "") =>
  (url.match(/\.([a-z0-9]+)(?:\?|#|$)/i)?.[1] || "FILE").toUpperCase();

const getFileName = (s = "") => {
  try {
    const u = new URL(s, window.location.origin);
    return decodeURIComponent(u.pathname.split("/").pop() || "File");
  } catch {
    const parts = String(s).split(/[/?#]/);
    return decodeURIComponent(parts.pop() || "File");
  }
};

const resolveSrc = (v) => (v?.startsWith("http") ? v : `${IMAGE_CDN}${v}?size=normal`);
const resolveRaw = (v) => (v?.startsWith("http") ? v : `${IMAGE_CDN}${v}`);

const proofRequiredOptions = [
  { id: 0, name: "None" },
  { id: 1, name: "Email Proof" },
  { id: 2, name: "Pre Production Sample" },
  { id: 3, name: "Digital Photo" },
];

const repeatLogoOptions = [
  { id: 1, name: "Yes" },
  { id: 2, name: "No" },
];

export const resolveSalesOrderImprintType = (artwork = {}, decorationMethodName = "") => {
  const imprintType = String(
    artwork?.ImprintType ?? artwork?.imprintType ?? ""
  ).trim();
  const isRoutingToken =
    /^supplier\|\$/i.test(imprintType) ||
    /^thirdparty\|/i.test(imprintType);

  if (!isRoutingToken && imprintType) return imprintType;

  const methodName = String(decorationMethodName || "").trim();
  return methodName || imprintType || "-";
};

const buildProofRowWrapper = (proofRow = {}, artwork = {}, location = "") => ({
  proof: proofRow,
  decoration: { Location: location || artwork?.Location || "" },
  product: {
    Name: artwork?.LogoName || artwork?.Name || "Artwork Proof",
  },
});

const getLatestRejectedProofComment = (proofRow = {}) => {
  const auditTrail = Array.isArray(proofRow?.auditTrail) ? [...proofRow.auditTrail] : [];
  const latestRejectedEntry = [...auditTrail]
    .reverse()
    .find((entry) => String(entry?.state || entry?.type || "").toLowerCase() === "rejected");
  const latestComment = String(
    latestRejectedEntry?.comments || latestRejectedEntry?.comment || ""
  ).trim();
  if (latestComment) return latestComment;
  return String(proofRow?.comments || "").trim();
};

const deriveNameFromEmail = (email) => {
  const prefix = String(email || "").split("@")[0] || "";
  return prefix.trim() || "Client";
};

const ArtWorkDetails = ({
  artwork = {},
  location,
  proofRow = null,
  proofMappingId = null,
  proofReplacement = null,
  proofDecisionContext = {},
  showPerLocationProofApproval = true,
  hasCompleteApprovalPackage = false,
  onProofDecisionUpdated,
  compactGeneratedSalesOrder = false,
  decorationProvider = null,
  decorationMethodName = "",
}) => {
  const rawPath = artwork?.Image || artwork?.FileUrl || "";
  const raw = rawPath ? resolveRaw(rawPath) : "";
  const src = rawPath ? resolveSrc(rawPath) : "";
  const name = rawPath ? getFileName(rawPath) : "";
  const ext = rawPath ? getExt(rawPath) : "";
  const showImg = rawPath && IMG_EXT_RX.test(rawPath);
  const showPdf = rawPath && isPdfFile(rawPath);

  const [activeProofRow, setActiveProofRow] = React.useState(proofRow || null);
  const [comment, setComment] = React.useState("");
  const [clientEmail, setClientEmail] = React.useState(() => getStoredEmail());
  const [submitting, setSubmitting] = React.useState(false);
  const [pdfOpen, setPdfOpen] = React.useState(false);
  const [pdfSrc, setPdfSrc] = React.useState("");
  const [pdfName, setPdfName] = React.useState("");
  const [requestChangesOpen, setRequestChangesOpen] = React.useState(false);
  const [pendingRejectContext, setPendingRejectContext] = React.useState(null);

  React.useEffect(() => {
    setActiveProofRow(proofRow || null);
  }, [proofRow]);

  const openPdf = React.useCallback((nextSrc, nextName) => {
    setPdfSrc(nextSrc || "");
    setPdfName(nextName || "");
    setPdfOpen(true);
  }, []);

  const proofCardLabels = React.useMemo(
    () => ({
      itemName: artwork?.LogoName || "Mapped Proof",
      clientEmailLabel: "Your email (for proof review record)",
    }),
    [artwork?.LogoName]
  );

  const generatedProofPreviewSx = React.useMemo(
    () =>
      compactGeneratedSalesOrder
        ? {
            maxWidth: "100%",
            "@media (min-width:900px)": {
              maxHeight: 200,
              height: 200,
            },
          }
        : undefined,
    [compactGeneratedSalesOrder]
  );

  const handleProofCommentChange = React.useCallback((_, value) => {
    setComment(value);
  }, []);

  const handleProofClientEmailChange = React.useCallback((_, value) => {
    setClientEmail(value);
  }, []);

  const proofRequiredText =
    proofRequiredOptions.find((option) => option.id === Number(artwork?.ProofRequired))?.name || "-";
  const repeatLogoText =
    repeatLogoOptions.find((option) => option.id === Number(artwork?.RepeatLogo))?.name || "-";

  // Use the stored mapping mode, with the proof row as a legacy fallback.
  // Unknown modes must not silently become an interactive per-location approval.
  const mappedApprovalMode = String(
    proofReplacement?.mapping?.approvalMode || activeProofRow?.approvalMode || ""
  ).trim().toLowerCase();
  const isCompletePackageProof = mappedApprovalMode === "complete_package";
  const isPerLocationProof = mappedApprovalMode === "per_location";
  const hasMappedProof = shouldShowMappedArtworkProof({
    hasProof: Boolean(activeProofRow?.filePath || activeProofRow?.proofFilePath),
    isCompletePackageProof,
    isPerLocationProof,
    canShowPerLocation: showPerLocationProofApproval,
    hasCompleteApprovalPackage,
  });
  const userApprovalState = String(activeProofRow?.approvals?.user?.state || "pending").toLowerCase();
  const clientApprovalState = String(activeProofRow?.approvals?.client?.state || "pending").toLowerCase();
  const proofStatus = String(activeProofRow?.proofStatus || activeProofRow?.status || "").toLowerCase();
  const isRejectedProof =
    userApprovalState === "rejected" ||
    clientApprovalState === "rejected" ||
    proofStatus === "rejected";
  const isFullyApprovedProof =
    proofStatus === "approved" ||
    (userApprovalState === "approved" && clientApprovalState === "approved");
  const isBlockingProof = hasMappedProof && !isRejectedProof && !isFullyApprovedProof;
  const canSubmitDecisions = Boolean(
    isPerLocationProof &&
    !hasCompleteApprovalPackage &&
    showPerLocationProofApproval &&
    proofMappingId &&
    activeProofRow?.id &&
    !isFullyApprovedProof &&
    !isRejectedProof &&
    !submitting
  );
  const isInternal = proofDecisionContext?.isInternal === true;
  const proofCardRow = React.useMemo(
    () => buildProofRowWrapper(activeProofRow || {}, artwork, location),
    [activeProofRow, artwork, location]
  );
  const latestRejectedComment = React.useMemo(
    () => getLatestRejectedProofComment(activeProofRow || {}),
    [activeProofRow]
  );

  const commitProofDecision = React.useCallback(
    async ({ mappingId, incomingProofId, state, nextComment }) => {
      if (!showPerLocationProofApproval || !mappingId || !incomingProofId) {
        toast.error("Artwork proof mapping is unavailable for this row.");
        return;
      }

      const trimmedComment = String(nextComment || "").trim();
      if (state === "rejected" && !trimmedComment) {
        toast.error("Please add a comment before requesting changes.");
        return;
      }

      const nextClientEmail = String(clientEmail || "").trim();
      if (!isInternal && !isValidEmail(nextClientEmail)) {
        toast.error("Please enter a valid email to record this proof decision.");
        return;
      }

      const payload = {
        state,
        comments: trimmedComment,
        by: isInternal
          ? {
              name: proofDecisionContext?.internalName || "Internal User",
              email: proofDecisionContext?.internalEmail || "",
            }
          : {
              name: deriveNameFromEmail(nextClientEmail),
              email: nextClientEmail,
            },
        actor: isInternal
          ? {
              type: "internal",
              email: proofDecisionContext?.internalEmail || "",
            }
          : {
              type: "client",
              email: nextClientEmail,
            },
      };

      try {
        setSubmitting(true);
        const response = await patchArtworkProofMappingClientApproval(mappingId, incomingProofId, payload);
        const nextProof = response?.proof || response?.mapping?.proofRow || null;
        if (nextProof) {
          setActiveProofRow(nextProof);
          onProofDecisionUpdated?.({
            mappingId,
            proof: nextProof,
            mapping: response?.mapping || proofReplacement?.mapping || null,
            response,
          });
        }
        if (Array.isArray(response?.sideEffects?.warnings) && response.sideEffects.warnings.length > 0) {
          console.warn("[ArtWorkDetails] artwork proof side effects warnings:", response.sideEffects.warnings);
        }
        if (!isInternal && isValidEmail(nextClientEmail)) {
          setStoredEmail(nextClientEmail);
        }
        setComment("");
        toast.success(
          state === "approved"
            ? "Proof approved."
            : "Changes requested. Our team will review and upload an updated proof."
        );
      } catch (error) {
        console.error("[ArtWorkDetails] artwork proof decision failed:", error);
        toast.error(error?.response?.data?.message || "Failed to save the proof decision.");
      } finally {
        setSubmitting(false);
      }
    },
    [
      clientEmail,
      isInternal,
      onProofDecisionUpdated,
      proofDecisionContext?.internalEmail,
      proofDecisionContext?.internalName,
      proofReplacement?.mapping,
      showPerLocationProofApproval,
    ]
  );

  const handleSubmitDecision = React.useCallback(
    async (mappingId, incomingProofId, state) => {
      if (state === "rejected") {
        setPendingRejectContext({ mappingId, incomingProofId });
        setRequestChangesOpen(true);
        return;
      }
      await commitProofDecision({
        mappingId,
        incomingProofId,
        state,
        nextComment: comment,
      });
    },
    [comment, commitProofDecision]
  );

  const handleConfirmRequestChanges = React.useCallback(async () => {
    if (!pendingRejectContext) return;
    await commitProofDecision({
      mappingId: pendingRejectContext.mappingId,
      incomingProofId: pendingRejectContext.incomingProofId,
      state: "rejected",
      nextComment: comment,
    });
    setRequestChangesOpen(false);
    setPendingRejectContext(null);
  }, [comment, commitProofDecision, pendingRejectContext]);

  const handleCloseRequestChanges = React.useCallback(() => {
    if (submitting) return;
    setRequestChangesOpen(false);
    setPendingRejectContext(null);
  }, [submitting]);

  return (
    <>
      <Grid
        container
        className={[
          styles.footerContainer,
          compactGeneratedSalesOrder ? styles.generatedSalesOrderProofReview : "",
        ].filter(Boolean).join(" ")}
        id={proofMappingId ? `artwork-proof-mapping-${proofMappingId}` : undefined}
        data-artwork-proof-blocking={isBlockingProof ? "true" : undefined}
        data-artwork-proof-rejected={isRejectedProof ? "true" : undefined}
        sx={{
          alignItems: "flex-start",
          flexWrap: { xs: "wrap", md: "nowrap" },
          gap: 2,
          scrollMarginTop: "112px",
        }}
      >
        <Grid
          className={styles.Artworks}
          sx={{
            width: { xs: "100%", md: hasMappedProof ? "38%" : "50%" },
          }}
        >
          {decorationProvider && (
            <Box className={styles.container}>
              <Typography className={styles.title} color={TEXT_GREY} variant="body2">
                Decoration Provider
              </Typography>
              <Typography className={styles.value} color={TEXT_GREY} variant="body2">
                {decorationProvider}
              </Typography>
            </Box>
          )}
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant="body2">
              Proof Required
            </Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant="body2">
              {proofRequiredText}
            </Typography>
          </Box>
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant="body2">
              Logo Name
            </Typography>
            <Typography
              className={styles.value}
              color={TEXT_GREY}
              variant="body2"
              sx={{ whiteSpace: "pre-line" }}
            >
              {artwork?.LogoName || "-"}
            </Typography>
          </Box>
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant="body2">
              Repeat Logo
            </Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant="body2">
              {repeatLogoText}
            </Typography>
          </Box>
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant="body2">
              Logo Location
            </Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant="body2">
              {location || "-"}
            </Typography>
          </Box>
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant="body2">
              Logo Size
            </Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant="body2">
              {artwork?.LogoSize || "-"}
            </Typography>
          </Box>
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant="body2">
              Color of Logo
            </Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant="body2">
              {artwork?.LogoColor || "-"}
            </Typography>
          </Box>
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant="body2">
              Imprint Type
            </Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant="body2">
              {resolveSalesOrderImprintType(artwork, decorationMethodName)}
            </Typography>
          </Box>
        </Grid>

        <Grid
          className={[
            styles.totalPriceContainer,
            compactGeneratedSalesOrder ? styles.generatedSalesOrderTotalPriceContainer : "",
          ].filter(Boolean).join(" ")}
          sx={{
            width: { xs: "100%", md: hasMappedProof ? "58%" : "45%" },
            maxWidth: { xs: "100%", md: hasMappedProof ? "58%" : "45%" },
            border: hasMappedProof ? "none" : undefined,
            padding: hasMappedProof ? 0 : undefined,
          }}
        >
          {hasMappedProof ? (
            <Grid container spacing={0}>
              {isRejectedProof ? (
                <Alert severity="warning" sx={{ mb: 2, width: "100%" }}>
                  <Typography variant="body2" fontWeight={700}>
                    Changes requested. Our team will upload an updated proof for review.
                  </Typography>
                  {latestRejectedComment ? (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {latestRejectedComment}
                    </Typography>
                  ) : null}
                </Alert>
              ) : null}

              {hasMappedProof ? (
                <Box sx={{ mt: 0, flex: "100%", mb: "5px", display: "flex", gap: 1 }}>
                  <Chip
                    label={isCompletePackageProof ? "Included in proof package" : "Mapped Proof"}
                    color="primary"
                    size="small"
                    sx={{ width: "fit-content" }}
                  />
                  {/*proofReplacement?.sourceType ? (
                    <Typography variant="caption" color="text.secondary">
                      Source: {proofReplacement.sourceType}
                      {proofReplacement?.sourceProvider ? ` • ${proofReplacement.sourceProvider}` : ""}
                    </Typography>
                  ) : null*/}
                  {raw ? (
                    <Typography variant="caption">
                      <a href={raw} target="_blank" rel="noopener noreferrer">
                        View original artwork
                      </a>
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Original artwork preview is unavailable for this row.
                    </Typography>
                  )}
                </Box>
              ) : null}

              <ProofReviewCard
                currentPoId={proofMappingId}
                row={proofCardRow}
                gridClassName={compactGeneratedSalesOrder ? styles.generatedSalesOrderMappedProofGrid : ""}
                commentValue={comment}
                onChangeComment={handleProofCommentChange}
                onChangeClientEmail={handleProofClientEmailChange}
                clientEmail={clientEmail}
                isInternal={isInternal}
                internalEmail={proofDecisionContext?.internalEmail || ""}
                isSubmitting={submitting}
                onSubmitDecision={handleSubmitDecision}
                onOpenPdf={openPdf}
                readOnly={!canSubmitDecisions}
                disabled={submitting}
                requireCommentForChanges={false}
                hideHeader={compactGeneratedSalesOrder}
                previewSx={generatedProofPreviewSx}
                refinedCustomerActions={compactGeneratedSalesOrder}
                labels={proofCardLabels}
              />
            </Grid>
          ) : rawPath ? (
            <>
              {showImg ? (
                <Box
                  className={styles.primaryImageContainer}
                  sx={{
                    border: "1px dashed #bdbdbd",
                    borderRadius: "8px",
                    width: "100%",
                    height: 200,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "#fafafa",
                    overflow: "hidden",
                    cursor: "pointer",
                  }}
                  onClick={() => window.open(raw, "_blank", "noopener,noreferrer")}
                >
                  <img
                    src={src}
                    alt={artwork?.LogoName || "Artwork preview"}
                    className={styles.primaryImage}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    loading="eager"
                    decoding="async"
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <SmartProofPreview
                    filePath={raw}
                    fileName={name}
                    previewImageUrl={artwork?.previewImageUrl || artwork?.previewUrl}
                    previewStatus={artwork?.previewStatus}
                    previewKind={artwork?.previewKind}
                    disabled={false}
                    fitContainer
                    lazyPdfPreview={showPdf}
                    pdfRenderWidth={showPdf ? 200 : undefined}
                    pdfRenderHeight={showPdf ? 240 : undefined}
                    onPreviewOpen={(filePath) => {
                      window.open(filePath || raw, "_blank", "noopener,noreferrer");
                    }}
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      minWidth: 0,
                      height: showPdf ? 240 : 220,
                      maxHeight: showPdf ? 240 : 220,
                      mx: 0,
                      boxSizing: "border-box",
                      overflow: "hidden",
                      borderRadius: "8px",
                      bgcolor: "#fafafa",
                      display: "grid",
                      placeItems: "center",
                    }}
                  />
                </Box>
              )}

              <Typography className={styles.printHidden} variant="caption" component="div" sx={{ mt: 1, textAlign: "center" }}>
                <a href={raw} target="_blank" rel="noopener noreferrer">
                  Click to preview
                </a>
              </Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.7, textAlign: "center" }}>
              No artwork image provided
            </Typography>
          )}
        </Grid>
      </Grid>

      {isPdfUrl(pdfSrc) ? (
        <PdfViewerDialog
          open={pdfOpen}
          src={pdfSrc}
          fileName={pdfName}
          onClose={() => setPdfOpen(false)}
        />
      ) : null}

      <Dialog className={styles.printHidden} open={requestChangesOpen} onClose={handleCloseRequestChanges} fullWidth maxWidth="sm">
        <DialogTitle>Request changes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="What needs to change?"
            placeholder="Share the proof changes you want to see."
            fullWidth
            multiline
            minRows={4}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRequestChanges} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmRequestChanges}
            disabled={submitting || !String(comment || "").trim()}
          >
            Send request changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ArtWorkDetails;
